import { ImageLightbox } from '@/components/ui/image-lightbox';
import { ScrollableImageIndicator } from '@/components/ui/scrollable-image-indicator';
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll';
import { cn } from '@/utils/cn';
import {
  animate,
  motion,
  MotionValue,
  useMotionValue,
  useMotionValueEvent,
} from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

// ドレスコード画像
const dressCodeExamples = [
  { src: '/images/dresscode/examples/sampleClothes.webp', alt: 'ドレスコードサンプル', type: 'example' },
  { src: '/images/dresscode/examples/IMG_5916.webp', alt: 'ドレスコード例1', type: 'example' },
  { src: '/images/dresscode/examples/IMG_5918.webp', alt: 'ドレスコード例2', type: 'example' },
  { src: '/images/dresscode/examples/IMG_1213.webp', alt: 'ドレスコード例4', type: 'example' },
  { src: '/images/dresscode/examples/IMG_1430.webp', alt: 'ドレスコード例5', type: 'example' },

];

// マスクエフェクト用の定数
const left = `0%`;
const right = `100%`;
const leftInset = `10%`;
const rightInset = `90%`;
const transparent = `rgba(0, 0, 0, 0)`;
const opaque = `rgba(0, 0, 0, 1)`;

function useScrollOverflowMask(scrollXProgress: MotionValue<number>) {
  const maskImage = useMotionValue(
    `linear-gradient(90deg, ${opaque}, ${opaque} ${left}, ${opaque} ${rightInset}, ${transparent})`
  );

  useMotionValueEvent(scrollXProgress, 'change', (value) => {
    if (value === 0) {
      // 最初の位置：右側だけフェード
      animate(
        maskImage,
        `linear-gradient(90deg, ${opaque}, ${opaque} ${left}, ${opaque} ${rightInset}, ${transparent})`
      );
    } else if (value === 1) {
      // 最後の位置：左側だけフェード
      animate(
        maskImage,
        `linear-gradient(90deg, ${transparent}, ${opaque} ${leftInset}, ${opaque} ${right}, ${opaque})`
      );
    } else if (
      scrollXProgress.getPrevious() === 0 ||
      scrollXProgress.getPrevious() === 1
    ) {
      // 中間位置：両側フェード
      animate(
        maskImage,
        `linear-gradient(90deg, ${transparent}, ${opaque} ${leftInset}, ${opaque} ${rightInset}, ${transparent})`
      );
    }
  });

  return maskImage;
}

export default function DressCode() {
  const ref = useRef<HTMLUListElement>(null);
  const {
    scrollXProgress,
    currentIndex,
    canScrollLeft,
    canScrollRight,
    scrollPrev,
    scrollNext
  } = useHorizontalScroll({ 
    containerRef: ref as React.RefObject<HTMLElement>, 
    totalItems: dressCodeExamples.length 
  });
  const maskImage = useScrollOverflowMask(scrollXProgress);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
  const [isOpen, setIsOpen] = useState(false);

  // モーダル用のハンドラー
  const handleImageSelect = useCallback((index: number) => {
    setSelectedImageIndex(index);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => setSelectedImageIndex(null), 200);
  }, []);

  return (
    <div className="relative w-full max-w-6xl mx-auto fairy-card bg-white/50 backdrop-blur-md rounded-3xl p-4 sm:p-6 md:p-8 shadow-lg">
      {/* タイトル */}
      <h2 className="text-3xl md:text-4xl font-dancing text-center text-gray-800 mb-12">
        Dress Code
      </h2>

      {/* ホスト画像（1枚の画像を中央に配置） */}
      <div className="flex justify-center mb-4 max-w-3xl mx-auto">
        <div className="bg-gradient-orange-to-green">
          <img
            src="/images/dresscode/hosts/kenty-mikasan.webp"
            alt="新郎新婦のドレスコード例"
            className="object-contain rounded-lg"
          />
        </div>
      </div>

      {/* 説明文 */}
      <div className="text-center">
        <p className="text-lg text-gray-700 font-fairy">
          沖縄らしいカジュアルなスタイルでお越しください
        </p>
        <p className="text-sm text-gray-600 mt-2 font-fairy">
          かりゆしウェアやアロハシャツ、ワンピースなど、リラックスした服装を推奨しています
        </p>
      </div>

      {/* スクロールギャラリーコンテナ */}
      <div className="relative mt-6">
        {/* 左矢印ボタン */}
        {canScrollLeft && (
          <button
            onClick={scrollPrev}
            className={cn(
              "absolute left-0 top-1/2 -translate-y-1/2 z-10",
              "bg-white/80 hover:bg-white text-gray-700",
              "rounded-full p-2 shadow-lg transition-all duration-300",
              "backdrop-blur-sm border border-gray-200",
              "hover:scale-110 active:scale-95",
              "md:opacity-70 md:hover:opacity-100"
            )}
            aria-label="前の画像"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {/* 右矢印ボタン */}
        {canScrollRight && (
          <button
            onClick={scrollNext}
            className={cn(
              "absolute right-0 top-1/2 -translate-y-1/2 z-10",
              "bg-white/80 hover:bg-white text-gray-700",
              "rounded-full p-2 shadow-lg transition-all duration-300",
              "backdrop-blur-sm border border-gray-200",
              "hover:scale-110 active:scale-95",
              "md:opacity-70 md:hover:opacity-100"
            )}
            aria-label="次の画像"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}

        {/* スクロールギャラリー - タッチスワイプ対応 */}
        <motion.ul
          ref={ref}
          className="flex list-none h-80 overflow-x-scroll py-5 gap-6 px-12 scrollbar-hide snap-x snap-mandatory touch-pan-x"
          style={{
            maskImage: maskImage as unknown as string,
            WebkitMaskImage: maskImage as unknown as string,
            WebkitOverflowScrolling: 'touch',
          }}
          onTouchStart={(e) => {
            const touch = e.touches[0];
            const startX = touch.clientX;
            const scrollLeft = ref.current?.scrollLeft || 0;

            const handleTouchMove = (e: TouchEvent) => {
              const touch = e.touches[0];
              const diff = startX - touch.clientX;
              if (ref.current) {
                ref.current.scrollLeft = scrollLeft + diff;
              }
            };

            const handleTouchEnd = () => {
              document.removeEventListener('touchmove', handleTouchMove);
              document.removeEventListener('touchend', handleTouchEnd);
            };

            document.addEventListener('touchmove', handleTouchMove, { passive: true });
            document.addEventListener('touchend', handleTouchEnd);
          }}
        >
        {dressCodeExamples.map((item, index) => (
          <li
            key={index}
            className="flex-shrink-0 w-56 snap-center cursor-pointer"
            onClick={() => handleImageSelect(index)}
          >
            <div className="w-full h-full rounded-lg overflow-hidden shadow-lg bg-white hover:shadow-xl transition-shadow">
              <img
                src={item.src}
                alt={item.alt}
                className="w-full h-full object-cover"
              />
            </div>
          </li>
        ))}
      </motion.ul>
      </div>

      {/* ページインジケーター - 画像の下に配置 */}
      <div className="flex justify-center items-center mt-4">
        <ScrollableImageIndicator
          total={dressCodeExamples.length}
          current={currentIndex}
          scrollContainerRef={ref as React.RefObject<HTMLElement>}
        />
      </div>

      {/* フルスクリーンライトボックス */}
      <ImageLightbox
        images={dressCodeExamples}
        selectedIndex={selectedImageIndex}
        isOpen={isOpen}
        onClose={handleClose}
        onIndexChange={setSelectedImageIndex}
        slideDirection={slideDirection}
        onSlideDirectionChange={setSlideDirection}
      />
    </div>
  );
}
